import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

/**
 * Reusable AI panel driving 5 astrology tabs (turning-points / muhurta / dasha-transit / remedies / good-alerts).
 * Each tab passes its own `topic` + list of `subtopics`.
 */
export default function AstroTopicPanel({ profileId, topic, title, subtopics, description }) {
  const t = useT();
  const [subtopic, setSubtopic] = useState(subtopics[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try { setData(await api.astroTopic(profileId, topic, subtopic)); }
    catch (err) {
      console.error("astro topic failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, [profileId, topic, subtopic]);

  useEffect(() => { load(); }, [load]);

  const renderInline = (text) => {
    // Convert **bold** and *italic* segments to React nodes
    const parts = [];
    let rest = text;
    let idx = 0;
    while (rest.length) {
      const b = rest.indexOf("**");
      if (b < 0) { parts.push(rest); break; }
      if (b > 0) parts.push(rest.slice(0, b));
      const end = rest.indexOf("**", b + 2);
      if (end < 0) { parts.push(rest.slice(b)); break; }
      parts.push(<strong key={`b${idx++}`} className="text-[#F0E7C8]">{rest.slice(b + 2, end)}</strong>);
      rest = rest.slice(end + 2);
    }
    return parts;
  };

  const render = (txt) => {
    if (!txt) return null;
    const lines = txt.split("\n");
    const out = [];
    let list = null; // {type: 'ul'|'ol', items: []}
    const flushList = () => {
      if (!list) return;
      const Tag = list.type === "ol" ? "ol" : "ul";
      out.push(
        <Tag key={`list-${out.length}`}
             className={`${list.type === "ol" ? "list-decimal" : "list-disc"} ml-6 space-y-1 my-2`}>
          {list.items.map((it, idx) => (
            <li key={`li-${idx}`} className="text-sm text-[#E6E1D6] leading-relaxed">{renderInline(it)}</li>
          ))}
        </Tag>,
      );
      list = null;
    };
    lines.forEach((l, i) => {
      const k = `L${i}-${l.slice(0, 24)}`;
      const trimmed = l.trim();
      const isBullet = l.startsWith("- ") || l.startsWith("• ") || l.startsWith("* ");
      const numMatch = l.match(/^(\d+)[.)]\s+(.*)$/);
      if (isBullet) {
        if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; }
        list.items.push(l.slice(2)); return;
      }
      if (numMatch) {
        if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; }
        list.items.push(numMatch[2]); return;
      }
      flushList();
      if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
        out.push(<hr key={k} className="my-3 border-[#2A2D35]" />); return;
      }
      if (l.startsWith("### ")) { out.push(<h4 key={k} className="font-heading text-base text-[#A8896C] mt-3 mb-1">{renderInline(l.slice(4))}</h4>); return; }
      if (l.startsWith("## ")) { out.push(<h3 key={k} className="font-heading text-lg text-[#D4AF37] mt-4 mb-2">{renderInline(l.slice(3))}</h3>); return; }
      if (l.startsWith("# ")) { out.push(<h2 key={k} className="font-heading text-xl text-[#E6E1D6] mt-4 mb-2">{renderInline(l.slice(2))}</h2>); return; }
      if (!l.trim()) { out.push(<div key={k} className="h-2" />); return; }
      out.push(<p key={k} className="text-sm text-[#E6E1D6] leading-relaxed font-light">{renderInline(l)}</p>);
    });
    flushList();
    return out;
  };

  return (
    <div className="space-y-5" data-testid={`topic-panel-${topic}`}>
      <div className="border-l-4 border-[#D4AF37] pl-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">{title}</div>
        {description && <div className="text-[10px] text-[#6E6B64] font-mono mt-1">{description}</div>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">{t("Focus")}</div>
          <Select value={subtopic} onValueChange={setSubtopic}>
            <SelectTrigger data-testid={`topic-${topic}-subtopic`}
                           className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-72">
              {subtopics.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid={`topic-${topic}-refresh`} onClick={load} disabled={loading}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> {t("Loading…")}</>
                    : <><Sparkles size={14} className="mr-2" /> {t("Generate")}</>}
        </Button>
      </div>

      {loading && <div className="text-[#A19E95] font-mono text-sm">{t("Loading…")}</div>}

      {data && !loading && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-1"
             data-testid={`topic-${topic}-content`}>
          {render(data.content)}
        </div>
      )}
    </div>
  );
}

// Sub-topic lists for each of the 5 new tabs
export const TURNING_POINT_SUBS = [
  { key: "marriage", label: "Marriage" }, { key: "career", label: "Career" },
  { key: "business", label: "Business" }, { key: "investments", label: "Investments" },
  { key: "property", label: "Buying Property" }, { key: "childbirth", label: "Childbirth" },
  { key: "travel", label: "Travel" }, { key: "education", label: "Education" },
  { key: "retirement", label: "Retirement" },
];
export const MUHURTA_SUBS = [
  { key: "marriage-muhurta", label: "Marriage muhurta" },
  { key: "housewarming", label: "Housewarming (Griha Pravesh)" },
  { key: "business-opening", label: "Business opening" },
  { key: "vehicle-purchase", label: "Vehicle purchase" },
  { key: "naming", label: "Naming ceremony (Namakarana)" },
  { key: "surgery", label: "Surgery timing" },
  { key: "travel", label: "Travel timing" },
  { key: "contract", label: "Contract signing" },
  { key: "investment", label: "Investment timing" },
];
export const DASHA_TRANSIT_SUBS = [
  { key: "current-maha", label: "Current Mahadasha effects" },
  { key: "antardasha", label: "Antardasha results" },
  { key: "pratyantardasha", label: "Pratyantardasha trends" },
  { key: "major-transits", label: "Major transit effects" },
  { key: "sade-sati", label: "Saturn Sade Sati analysis" },
  { key: "jupiter-transit", label: "Jupiter transit" },
  { key: "rahu-ketu-transit", label: "Rahu-Ketu transit" },
  { key: "annual", label: "Annual horoscope" },
  { key: "monthly", label: "Monthly forecast" },
  { key: "yearly", label: "Yearly forecast" },
];
export const REMEDY_SUBS = [
  { key: "mantras", label: "Mantras" }, { key: "donations", label: "Donations (Dana)" },
  { key: "fasting", label: "Fasting (Vrata)" }, { key: "gemstones", label: "Gemstones (with caution)" },
  { key: "yantras", label: "Yantras" }, { key: "temple", label: "Temple visits" },
  { key: "homas", label: "Homas / Yajnas" }, { key: "rudraksha", label: "Rudraksha" },
  { key: "daily-practices", label: "Daily spiritual practices" },
];
export const GOOD_ALERT_SUBS = [
  { key: "next-90d", label: "Auspicious windows — next 90 days" },
  { key: "next-30d", label: "Auspicious windows — next 30 days" },
  { key: "sarvartha-siddhi", label: "Sarvartha Siddhi Yoga days" },
  { key: "guru-pushya", label: "Guru Pushya / Ravi Pushya days" },
  { key: "amrita-siddhi", label: "Amrita Siddhi Yoga days" },
];
